import { Sparkles, Award, Leaf } from "lucide-react";
import { useScrollReveal } from "../lib/useScrollReveal";
import { BRAND } from "../lib/brand";

const HIGHLIGHTS = [
  { Icon: Award, label: "Certified Yoga Instructor" },
  { Icon: Leaf, label: "Holistic Lifestyle Coaching" },
  { Icon: Sparkles, label: "Real, Lasting Transformation" },
];

export const About = ({ onBook }) => {
  const ref = useScrollReveal();
  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="relative py-24 md:py-36 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
        {/* Left — founder image */}
        <div className="lg:col-span-5 relative">
          <div className="reveal relative rounded-[32px] overflow-hidden shadow-[0_22px_60px_rgba(31,74,56,0.18)] aspect-[4/5] bg-[#1F4A38]/5">
            <img
              src={BRAND.founderImage}
              alt="Nitu Yadav — Founder & Yoga Instructor at Yogiinit"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-7 bg-gradient-to-t from-[#153326]/85 via-[#153326]/40 to-transparent text-[#F9F6F0]">
              <p className="text-xs tracking-[0.3em] uppercase text-[#D4A24C] mb-1.5">
                Founder · Instructor
              </p>
              <h3 className="font-serif-display text-3xl font-light">Nitu Yadav</h3>
            </div>
          </div>
          <div className="reveal hidden md:block absolute -bottom-6 -right-6 bg-[#D4A24C] text-[#153326] rounded-2xl px-6 py-4 shadow-xl">
            <p className="font-serif-display italic text-xl leading-tight">
              From Flab<br /> to Fab
            </p>
          </div>
        </div>

        {/* Right — copy */}
        <div className="lg:col-span-7">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            About Yogiinit
          </p>
          <h2
            data-testid="about-heading"
            className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light leading-[1.05]"
          >
            Real Transformation, <em className="italic">Rooted</em> in Practice.
          </h2>

          <div className="reveal mt-8 space-y-5 text-[#2C5F4A] leading-relaxed text-[15.5px] max-w-2xl">
            <p>
              At <strong className="text-[#153326]">Yogiinit</strong>, we help individuals achieve
              weight loss, flexibility, strength, posture correction and holistic
              wellness through expert-led yoga coaching. Founded by certified yoga
              instructor <strong className="text-[#153326]">Nitu Yadav</strong>, Yogiinit is designed
              for people who want real transformation — not just temporary fitness results.
            </p>
            <p>
              Our structured yoga programs combine traditional yoga techniques, modern
              fitness principles and personalized coaching to help you build a stronger
              body, calmer mind and healthier lifestyle.
            </p>
            <p>
              Whether you're looking for <em>weight loss yoga classes</em>, <em>private yoga
              sessions</em>, <em>online yoga coaching</em>, or <em>group yoga classes in Mumbai</em>,
              Yogiinit provides transformation-focused programs tailored to your goals.
            </p>
          </div>

          <div className="reveal mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            {HIGHLIGHTS.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-2xl bg-white border border-[#1F4A38]/10 px-4 py-3 shadow-[0_6px_22px_rgba(31,74,56,0.05)]"
              >
                <span className="h-9 w-9 rounded-xl bg-[#1F4A38]/8 text-[#1F4A38] flex items-center justify-center">
                  <Icon size={16} strokeWidth={1.8} />
                </span>
                <span className="text-sm font-medium text-[#153326]">{label}</span>
              </div>
            ))}
          </div>

          <p className="reveal mt-10 font-serif-display italic text-2xl md:text-3xl text-[#1F4A38]">
            From Flab to Fab — Transform Your Body, Mind &amp; Lifestyle.
          </p>

          <button
            data-testid="about-cta-btn"
            onClick={onBook}
            className="reveal mt-8 inline-flex items-center gap-2 bg-[#1F4A38] hover:bg-[#153326] text-[#F9F6F0] rounded-full px-8 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 shadow-lg hover:-translate-y-0.5"
          >
            Start Your Transformation
            <span>→</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default About;
